import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ChevronLeft, Star, Sun, Moon, Heart, Briefcase, 
  Sparkles, Lock, Crown, Calendar, Palette, Hash,
  Zap, ArrowRight, LogIn, User, RefreshCw
} from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIGN_ICONS = {
  aries: "♈", taurus: "♉", gemini: "♊", cancer: "♋",
  leo: "♌", virgo: "♍", libra: "♎", scorpio: "♏",
  sagittarius: "♐", capricorn: "♑", aquarius: "♒", pisces: "♓"
};

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center py-20">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
      className="mb-6"
    >
      <Sun className="h-16 w-16 text-amber-500" />
    </motion.div>
    <p className="text-gray-600 text-lg">Consultation des astres en cours...</p>
    <p className="text-gray-400 text-sm mt-2">Stella prépare ton horoscope personnalisé</p>
  </div>
);

const PremiumUpsell = ({ onNavigate }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="max-w-2xl mx-auto"
  >
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl border-2 border-amber-200 p-8 text-center shadow-xl">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full mb-6 shadow-lg">
        <Crown className="h-10 w-10 text-white" />
      </div>
      
      <h2 className="text-2xl font-bold text-gray-900 mb-3">
        Fonctionnalité Premium
      </h2>
      
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        L'horoscope du jour personnalisé est réservé aux membres Premium.
        Débloquez cette fonctionnalité et bien plus encore !
      </p>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          { icon: Star, text: "Horoscope quotidien personnalisé" },
          { icon: Sparkles, text: "Analyses approfondies illimitées" },
          { icon: Moon, text: "Accès à tous les modules premium" }
        ].map((item, i) => (
          <div key={i} className="flex flex-col items-center p-4 bg-white/60 rounded-xl">
            <item.icon className="h-6 w-6 text-amber-500 mb-2" />
            <span className="text-sm text-gray-700 text-center">{item.text}</span>
          </div>
        ))}
      </div>
      
      <Button
        onClick={() => onNavigate('/premium')}
        className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
        data-testid="upgrade-premium-btn"
      >
        <Crown className="h-5 w-5 mr-2" />
        Devenir Premium
        <ArrowRight className="h-5 w-5 ml-2" />
      </Button>
    </div>
  </motion.div>
);

const LoginPrompt = ({ onNavigate }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="max-w-2xl mx-auto"
  >
    <div className="bg-gradient-to-br from-sky-50 to-cyan-50 rounded-3xl border-2 border-sky-200 p-8 text-center shadow-xl">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-sky-400 to-cyan-500 rounded-full mb-6 shadow-lg">
        <User className="h-10 w-10 text-white" />
      </div>
      
      <h2 className="text-2xl font-bold text-gray-900 mb-3">
        Connexion Requise
      </h2>
      
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        Connectez-vous pour accéder à votre horoscope personnalisé du jour.
      </p>
      
      <Button
        onClick={() => onNavigate('/auth')}
        className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
        data-testid="login-prompt-btn"
      >
        <LogIn className="h-5 w-5 mr-2" />
        Se connecter
      </Button>
    </div>
  </motion.div>
);

const ProfilePrompt = ({ message, onNavigate }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="max-w-2xl mx-auto"
  >
    <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-3xl border-2 border-violet-200 p-8 text-center shadow-xl">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full mb-6 shadow-lg">
        <Calendar className="h-10 w-10 text-white" />
      </div>
      
      <h2 className="text-2xl font-bold text-gray-900 mb-3">
        Profil Incomplet
      </h2>
      
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        {message || "Veuillez compléter votre profil avec votre date de naissance pour accéder à l'horoscope personnalisé."}
      </p>
      
      <Button
        onClick={() => onNavigate('/profile')}
        className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
        data-testid="complete-profile-btn"
      >
        Compléter mon profil
        <ArrowRight className="h-5 w-5 ml-2" />
      </Button>
    </div>
  </motion.div>
);

const HoroscopeContent = ({ horoscope }) => {
  const content = horoscope.content;
  const sign = horoscope.sign;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto space-y-6"
    >
      {/* Header Card */}
      <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 rounded-3xl p-8 text-white shadow-2xl">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="text-7xl" data-testid="horoscope-sign-symbol">
            {sign.symbol}
          </div>
          <div className="text-center md:text-left flex-1">
            <p className="text-amber-100 text-sm mb-1">{horoscope.day_of_week} {horoscope.date}</p>
            <h2 className="text-3xl font-bold mb-2" data-testid="horoscope-greeting">
              {content.greeting}
            </h2>
            <div className="flex flex-wrap gap-2 justify-center md:justify-start">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                {sign.name}
              </span>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                {sign.element}
              </span>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                {sign.planet}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* General Message */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-amber-100 rounded-xl">
            <Sun className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 mb-2">Énergie du Jour</h3>
            <p className="text-gray-600 leading-relaxed" data-testid="horoscope-general">
              {content.general}
            </p>
          </div>
        </div>
      </div>
      
      {/* Three Domains Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Love */}
        <div className="bg-white rounded-2xl border border-pink-200 p-5 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-pink-100 rounded-lg">
              <Heart className="h-5 w-5 text-pink-500" />
            </div>
            <h3 className="font-bold text-gray-900">Amour</h3>
          </div>
          <p className="text-gray-600 text-sm" data-testid="horoscope-love">
            {content.love}
          </p>
        </div>
        
        {/* Work */}
        <div className="bg-white rounded-2xl border border-blue-200 p-5 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Briefcase className="h-5 w-5 text-blue-500" />
            </div>
            <h3 className="font-bold text-gray-900">Travail</h3>
          </div>
          <p className="text-gray-600 text-sm" data-testid="horoscope-work">
            {content.work}
          </p>
        </div>
        
        {/* Health */}
        <div className="bg-white rounded-2xl border border-green-200 p-5 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Sparkles className="h-5 w-5 text-green-500" />
            </div>
            <h3 className="font-bold text-gray-900">Bien-être</h3>
          </div>
          <p className="text-gray-600 text-sm" data-testid="horoscope-health">
            {content.health}
          </p>
        </div>
      </div>
      
      {/* Advice */}
      <div className="bg-gradient-to-r from-violet-500 to-purple-500 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <Star className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-bold mb-2">Conseil de Stella</h3>
            <p className="text-violet-100 leading-relaxed" data-testid="horoscope-advice">
              {content.advice}
            </p>
          </div>
        </div>
      </div>
      
      {/* Lucky Items */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-md">
          <Hash className="h-6 w-6 text-amber-500 mx-auto mb-2" />
          <p className="text-xs text-gray-500 mb-1">Chiffre porte-bonheur</p>
          <p className="text-2xl font-bold text-gray-900" data-testid="lucky-number">
            {content.lucky_number}
          </p>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-md">
          <Palette className="h-6 w-6 text-rose-500 mx-auto mb-2" />
          <p className="text-xs text-gray-500 mb-1">Couleur du jour</p>
          <p className="text-lg font-bold text-gray-900" data-testid="lucky-color">
            {content.lucky_color}
          </p>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-md">
          <Sparkles className="h-6 w-6 text-violet-500 mx-auto mb-2" />
          <p className="text-xs text-gray-500 mb-1">Humeur</p>
          <p className="text-lg font-bold text-gray-900" data-testid="mood">
            {content.mood}
          </p>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-md">
          <Zap className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
          <p className="text-xs text-gray-500 mb-1">Niveau d'énergie</p>
          <div className="flex items-center justify-center gap-1">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className={`w-2 h-4 rounded-sm ${
                  i < content.energy_level 
                    ? 'bg-gradient-to-t from-yellow-400 to-amber-400' 
                    : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1" data-testid="energy-level">
            {content.energy_level}/10
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const AstrozodiacHoroscopePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchHoroscope = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/astrozodiac/horoscope`, {
        withCredentials: true
      });
      setHoroscopeData(response.data);
    } catch (err) {
      console.error('Error fetching horoscope:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement de l\'horoscope');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (!authLoading) {
      fetchHoroscope();
    }
  }, [authLoading]);
  
  const renderContent = () => {
    if (authLoading || loading) {
      return <LoadingState />;
    }
    
    if (error) {
      return (
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={fetchHoroscope} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Réessayer
          </Button>
        </div>
      );
    }
    
    if (!horoscopeData) {
      return <LoadingState />;
    }
    
    // Check different states based on API response
    if (!horoscopeData.success && !horoscopeData.is_premium) {
      // Not logged in or not premium
      if (horoscopeData.message?.includes('connecté')) {
        return <LoginPrompt onNavigate={navigate} />;
      }
      return <PremiumUpsell onNavigate={navigate} />;
    }
    
    if (horoscopeData.success && horoscopeData.is_premium && !horoscopeData.horoscope) {
      // Premium but missing profile data
      return <ProfilePrompt message={horoscopeData.message} onNavigate={navigate} />;
    }
    
    if (horoscopeData.horoscope) {
      return <HoroscopeContent horoscope={horoscopeData.horoscope} />;
    }
    
    return <PremiumUpsell onNavigate={navigate} />;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-orange-50">
      {/* Decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 bg-amber-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-orange-200/20 rounded-full blur-3xl" />
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-amber-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link 
              to="/astrozodiac" 
              className="flex items-center gap-2 text-amber-600 hover:text-amber-700 transition-colors"
              data-testid="back-to-astrozodiac"
            >
              <ChevronLeft className="h-5 w-5" />
              <span className="font-semibold">ASTROZODIAC</span>
            </Link>
            <span className="text-gray-300">/</span>
            <div className="flex items-center gap-2">
              <Sun className="h-5 w-5 text-amber-500" />
              <span className="font-bold text-gray-900 tracking-wide">Horoscope du Jour</span>
            </div>
          </div>
          
          <div>
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <span className="text-gray-600 text-sm hidden sm:block">
                  {user?.first_name || user?.name}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={logout}
                  className="border-amber-200 text-amber-700 hover:bg-amber-50 rounded-xl"
                >
                  <User className="h-4 w-4 mr-2" />
                  Déconnexion
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/auth')}
                className="border-amber-200 text-amber-700 hover:bg-amber-50 rounded-xl"
                data-testid="login-btn"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Connexion
              </Button>
            )}
          </div>
        </div>
      </header>
      
      {/* Hero */}
      <section className="relative py-12 px-4">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full border border-amber-200 mb-6">
              <Crown className="h-4 w-4 text-amber-600" />
              <span className="text-amber-700 text-sm font-medium">Premium</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="text-gray-900">Ton</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 ml-2">
                Horoscope du Jour
              </span>
            </h1>
            
            <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
              Chaque jour, Stella consulte les astres pour te guider dans ta journée.
              Un horoscope personnalisé basé sur ton signe solaire.
            </p>
          </motion.div>
        </div>
        
        {/* Main Content */}
        <div className="px-4 pb-16">
          {renderContent()}
        </div>
      </section>
    </div>
  );
};

export default AstrozodiacHoroscopePage;
